import ReCAPTCHA from 'react-google-recaptcha';
import { Box, Button, Flex } from '@chakra-ui/react';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { DeviceSystem } from 'enums/shared/deviceSystem';
import { Form, Formik } from 'formik';
import useFormError from 'hooks/shared/useFormError';
import { OtpId } from 'model/modules/otp/types';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parsePhoneNumber } from 'react-phone-number-input';
import { useMount } from 'react-use';
import { useCheckPhoneMutation } from 'rtkQuery/sharespro/queries/login';

import LoginTemplate from 'presentation/components/modules/login/LoginTemplate';
import FormPhoneNumberInput from 'presentation/components/shared/form/FormPhoneNumberInput';
import { isSharesproFetchQueryError } from 'rtkQuery/sharespro/isSharesproFetchQueryError';
import { SharesproApiResponse } from 'rtkQuery/sharespro/types/sharesproApiResponse';
import { SharesproApiErrorCode } from 'enums/shared/sharesproApiErrorCode';
import { getOrCreateAttributionVisitorId } from 'utils/attribution';

interface FormValues {
  phone: string;
  captcha_token: string | null;
}

interface Props {
  goNext: (phone: string, otpId: OtpId) => void;
}

const LoginEnterPhoneContainer: FC<Props> = ({ goNext }) => {
  const { t } = useTranslation();

  const [fpHash, setFpHash] = useState<string>('');

  const [checkPhone, { isLoading }] = useCheckPhoneMutation();
  const [showCaptcha, setShowCaptcha] = useState(false);

  useMount(() => {
    FingerprintJS.load()
      .then(async (fp) => await fp.get())
      .then(({ visitorId }) => setFpHash(visitorId));
  });

  const handleFormError = useFormError<FormValues>();

  return (
    <LoginTemplate title={t('signInSharespro')}>
      <Formik<FormValues>
        initialValues={{
          phone: '',
          captcha_token: null,
        }}
        onSubmit={(values, formik) => {
          const phoneNumber = parsePhoneNumber(values.phone);

          if (!phoneNumber?.isValid()) {
            formik.setFieldError('phone', t('invalidPhoneNumber') ?? undefined);

            return;
          }

          if (phoneNumber?.number) {
            checkPhone({
              phone: phoneNumber?.number,
              app_id: fpHash,
              device_system: DeviceSystem.Web,
              visitor_id: getOrCreateAttributionVisitorId(),
              captcha_token: values.captcha_token,
            })
              .unwrap()
              .then((otpId) => {
                goNext(values.phone, otpId);
              })
              .catch((err) => {
                if (
                  isSharesproFetchQueryError(err) &&
                  (err.data as SharesproApiResponse<any>).error_code ===
                    SharesproApiErrorCode.CaptchaRequired
                ) {
                  setShowCaptcha(true);
                } else {
                  handleFormError(err, formik);
                }
              });
          }
        }}
      >
        {({ handleChange, values, submitForm, setFieldValue }) => (
          <Form>
            {!showCaptcha && (
              <Flex
                alignItems="flex-start"
                flexDirection={{ base: 'column', md: 'row' }}
                justifyContent="space-between"
              >
                <Box flex="1" w={{ base: 'full', md: 'unset' }}>
                  <FormPhoneNumberInput
                    name="phone"
                    onChange={handleChange}
                    value={values.phone}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        submitForm();
                      }
                    }}
                  />
                </Box>
                <Button
                  w={{ base: 'full', md: 'unset' }}
                  ml={{ base: '0', md: '20px' }}
                  mt={{ base: '20px', md: '0' }}
                  type="submit"
                  isLoading={isLoading}
                >
                  {t('continuePhrase')}
                </Button>
              </Flex>
            )}
            {showCaptcha && (
              <ReCAPTCHA
                sitekey={process.env.REACT_APP_CAPTCHA_SITE_KEY ?? ''}
                onChange={(token) => {
                  setFieldValue('captcha_token', token);
                  setTimeout(() => {
                    setShowCaptcha(false);
                    submitForm();
                  }, 0);
                }}
              />
            )}
          </Form>
        )}
      </Formik>
    </LoginTemplate>
  );
};

export default LoginEnterPhoneContainer;
